import React, { useId } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import {
  BRIGHT_HORIZONS_CREDITS_DONT_APPLY,
  BRIGHT_HORIZONS_CREDITS_DONT_APPLY_TEXT,
} from '@rover/components/src/callouts/CalloutBadge/CalloutBadge.constants';
import usePriceDisplayInfo from '@rover/components/src/cards/SearchResultCard/PriceColumn/usePriceDisplayInfo';
import { DEFAULT_CURRENCY_ISO } from '@rover/constants/src/i18n/language';
import Calendar from '@rover/icons/svg/calendar.svg';
import CheckCircleBlue from '@rover/icons/svg/check-circle-blue.svg';
import { Box, Flex, Heading, Text } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet/BottomSheet';
import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';
import useTheme from '@rover/kibble/themes/useTheme';
import useLocationContext from '@rover/react-lib/src/context/useLocationContext';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';

import ContactButton from '../../../components/ContactButton';
import { SitterProfileVersionOptions } from '../../constants';
import { useRevampBrightHorizonsBanner } from '../../hooks/useRevampBrightHorizonsBanner';
import { useRevampCta } from '../../hooks/useRevampCta';
import { useRevampCtaCommon } from '../../hooks/useRevampCtaCommon';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import { useRevampSelectedDates } from '../../hooks/useRevampSelectedDates';
import { BHSitterProfileBanner } from '../BHSitterProfileBanner/BHSitterProfileBanner';
import AdditionalRatesSection from '../ServiceSection/AdditionalRatesSection';

import { SitterProfileSeeAllRatesClicked } from './analyticsEvents';
import { AWAY_TEXT_COLOR, isServiceAway } from './awayService';
import RevampCancellationPolicy from './RevampCancellationPolicy';
import RevampCtaPtBanner from './RevampCtaPtBanner';
import { CTAPropsCommon } from './RevampServicesCta';
import ServiceIconBadge from './ServiceIconBadge';
import { getSitterSlugFromWebUrl, serviceIconToGraphic } from './utils';

type Props = CTAPropsCommon & {
  isOpen: boolean;
  onClose: () => void;
  onServicePress: () => void;
  onDatesPress: () => void;
  selectedDate: Date;
  selectedEndDate?: Date;
  hasDateRange?: boolean;
};

const RevampPriceDisplaySheet = ({
  isOpen,
  onClose,
  onServicePress,
  onDatesPress,
  priceTransparencyLevel,
  locale,
  petCount,
  petType,
  bookingFeePercentage,
  selectedDate,
  selectedEndDate,
  hasDateRange,
}: Props): JSX.Element => {
  const { i18n, t } = useLingui();
  const theme = useTheme();
  const headingId = useId();
  const { contactUrl, showSittersRates } = useRevampProfileContext();
  const {
    sitterData,
    serviceType,
    selectedService,
    cancellationPolicy,
    isBrightHorizonsUser,
    isSelectedServiceUnapproved,
    isContactUnavailable,
  } = useRevampCta();
  const { rate, price, petLabel } = useRevampCtaCommon(
    priceTransparencyLevel,
    petType,
    petCount,
    selectedService
  );
  const currentUser = useCurrentUser();
  const location = useLocationContext();

  const priceInCredits = rate?.priceInCredits != null ? Number(rate.priceInCredits) : null;
  const shouldShowPriceInCredits =
    isBrightHorizonsUser && !showSittersRates && priceInCredits != null;

  const { brightHorizonsBanner, isShowingBhBanner } = useRevampBrightHorizonsBanner(
    petType,
    petCount
  );

  const { formattedPrice, beforePriceText, afterPriceText, showBrightHorizonsCalloutBadge } =
    usePriceDisplayInfo({
      currencyCode: rate?.pricePerUnitObj.currencyIso || DEFAULT_CURRENCY_ISO,
      priceTransparencyLevel,
      locale,
      price: price ?? '',
      priceInCredits,
      shouldShowPriceInCredits,
      showFromText: !shouldShowPriceInCredits,
      unitAsSentence: rate?.unitAsSentence ?? '',
      isBHUser: isBrightHorizonsUser,
    });

  const { hasDates, datesLabel: selectedDatesLabel } = useRevampSelectedDates(
    locale,
    selectedDate,
    selectedEndDate,
    hasDateRange
  );
  const datesLabel = hasDates ? selectedDatesLabel : t`Add dates`;

  const ServiceIcon = selectedService ? serviceIconToGraphic[selectedService.icon] : undefined;

  const serviceDisplayName = selectedService?.displayName;
  const isAway = isServiceAway(selectedService);
  const awayText = t`Away`;
  const serviceName = isAway ? `${serviceDisplayName} (${awayText})` : serviceDisplayName;
  const serviceRowLabel = serviceDisplayName
    ? t`Change service, currently ${serviceName}`
    : t`Select a service`;

  const rowProps = {
    webAs: 'button' as const,
    alignItems: 'center' as const,
    gap: '4x' as const,
    px: '4x' as const,
    py: '4x' as const,
    width: '100%' as const,
    border: 'none',
    sx: { background: 'transparent', cursor: 'pointer', textAlign: 'left' },
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onRequestClose={onClose}
      closeOnDragDown
      overflowY="auto"
      headerId={headingId}
    >
      <Box data-testid="revamp-price-display-sheet">
        <A11yHiddenBox webAs="h2" id={headingId}>
          <Trans>Booking details</Trans>
        </A11yHiddenBox>

        {rate && price && !isSelectedServiceUnapproved && (
          <AdditionalRatesSection
            services={sitterData.services}
            petType={petType}
            petCount={petCount}
            showSittersRates={showSittersRates}
            isCtaModal
            isRevampVersion
            renderTrigger={(toggleModal) => (
              <Flex alignItems="baseline" flexWrap="wrap" gap="2x" mb="6x">
                <Flex
                  webAs="button"
                  flexWrap="wrap"
                  alignItems="baseline"
                  gap="1x"
                  border="none"
                  padding="0x"
                  data-testid="revamp-price-display-sheet-price"
                  aria-label={t`${beforePriceText} ${formattedPrice} ${afterPriceText} for ${petLabel}, see all rates`}
                  onPress={() => {
                    emitAnalyticsEvent(
                      new SitterProfileSeeAllRatesClicked({
                        serviceSlug: serviceType ?? '',
                        pageVariant: SitterProfileVersionOptions.REVAMP,
                      })
                    );
                    toggleModal();
                  }}
                  sx={{ background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
                >
                  {beforePriceText && (
                    <Text size="200" textColor="secondary" fontWeight="regular" aria-hidden="true">
                      {beforePriceText}
                    </Text>
                  )}
                  <Heading
                    size="400"
                    mb="0x"
                    textColor="price"
                    webProps={{ as: 'span', responsive: false }}
                    aria-hidden="true"
                    sx={{ textDecoration: 'underline' }}
                  >
                    {formattedPrice}
                  </Heading>
                  <Text size="200" textColor="secondary" fontWeight="regular" aria-hidden="true">
                    {t`${afterPriceText} for ${petLabel}`}
                  </Text>
                </Flex>
                {showBrightHorizonsCalloutBadge && (
                  <Badge
                    {...BRIGHT_HORIZONS_CREDITS_DONT_APPLY}
                    text={i18n._(BRIGHT_HORIZONS_CREDITS_DONT_APPLY_TEXT)}
                  />
                )}
              </Flex>
            )}
          />
        )}

        {isSelectedServiceUnapproved && (
          <Box pb="4x">
            <Badge
              boxProps={{ testID: 'cta-coming-soon-badge', alignSelf: 'flex-start' }}
              text={t`Coming Soon`}
              variant="default"
            />
          </Box>
        )}

        <Box borderRadius="8px" border={`1px solid ${theme.colors.separator.primary}`}>
          <Flex
            {...rowProps}
            data-testid="revamp-price-display-sheet-service"
            onPress={onServicePress}
            aria-haspopup="dialog"
            aria-label={serviceRowLabel}
            borderBottom={`1px solid ${theme.colors.separator.primary}`}
            gap="4x"
          >
            {ServiceIcon && (
              <ServiceIconBadge
                icon={ServiceIcon}
                serviceSlug={selectedService?.slug}
                testID="revamp-price-display-sheet-service-icon"
                isAway={isAway}
              />
            )}
            <Flex flexDirection="column" alignItems="flex-start" aria-hidden="true">
              <Flex alignItems="center" gap="2x">
                <Text
                  size="300"
                  fontWeight="semibold"
                  textColor={isAway ? AWAY_TEXT_COLOR : 'primary'}
                  data-testid="cta-service-name"
                >
                  {serviceDisplayName ?? t`Select a service`}
                </Text>
                {/* The check reads as a confirmation, which would contradict the dimming. */}
                {selectedService && !isAway && (
                  <CheckCircleBlue
                    width="20"
                    height="20"
                    aria-hidden="true"
                    data-testid="revamp-price-display-sheet-service-check"
                  />
                )}
              </Flex>
              {selectedService?.description && (
                <Text size="200" textColor={isAway ? AWAY_TEXT_COLOR : 'secondary'}>
                  {selectedService.description}
                </Text>
              )}
            </Flex>
          </Flex>

          <Flex
            {...rowProps}
            data-testid="revamp-price-display-sheet-dates"
            onPress={onDatesPress}
            aria-haspopup="dialog"
            aria-label={hasDates ? t`Change dates, ${datesLabel}` : t`Add dates`}
            borderBottom={`1px solid ${theme.colors.separator.primary}`}
          >
            <ServiceIconBadge
              icon={Calendar}
              testID="revamp-price-display-sheet-dates-icon"
              backgroundColor={theme.colors.neutral[200].toString()}
              iconColor={theme.colors.neutral[900].toString()}
            />
            <Flex flexDirection="column" alignItems="flex-start" aria-hidden="true">
              <Flex alignItems="center" gap="2x">
                <Text size="300" fontWeight="semibold" textColor="primary">
                  <Trans>Dates</Trans>
                </Text>
                {hasDates && (
                  <CheckCircleBlue
                    width="20"
                    height="20"
                    aria-hidden="true"
                    data-testid="revamp-price-display-sheet-dates-check"
                  />
                )}
              </Flex>
              <Text size="200" textColor="secondary">
                {datesLabel}
              </Text>
            </Flex>
          </Flex>
        </Box>

        {isShowingBhBanner && (
          <Box pt="4x">
            <BHSitterProfileBanner brightHorizonsBanner={brightHorizonsBanner} />
          </Box>
        )}

        <Box pt={isShowingBhBanner ? '0x' : '4x'} pb="3x">
          <ContactButton
            sitterInfo={sitterData}
            sitterSlug={getSitterSlugFromWebUrl(sitterData.webUrl)}
            contactUrl={contactUrl}
            isLoggedIn={currentUser.isAuthenticated}
            location={location}
            pagePosition="sidebar"
            shouldShowRevampLayout
            disabled={isContactUnavailable}
          />
        </Box>

        <RevampCancellationPolicy
          cancellationPolicy={cancellationPolicy}
          serviceName={selectedService?.displayName}
          cancellationPolicyDesc={selectedService?.meta?.cancellationPolicyDesc}
          providerId={sitterData.opk}
        />

        <RevampCtaPtBanner
          priceTransparencyLevel={priceTransparencyLevel}
          petCount={petCount}
          petType={petType}
          bookingFeePercentage={bookingFeePercentage}
          variant="sheet"
        />
      </Box>
    </BottomSheet>
  );
};

export default RevampPriceDisplaySheet;
